import { Controller } from "@hotwired/stimulus"

// The list floats above the dialog (fixed, placed from the field's position) so opening it never makes the dialog body
// scroll; it opens upward when there is no room below.
function place(panel, anchor) {
  const rect = anchor.getBoundingClientRect()
  const room = window.innerHeight - rect.bottom
  const height = Math.min(panel.scrollHeight, 288)
  panel.style.position = "fixed"
  panel.style.left = `${rect.left}px`
  panel.style.width = `${rect.width}px`
  panel.style.right = "auto"
  if (room < height + 16 && rect.top > room) {
    panel.style.top = "auto"
    panel.style.bottom = `${window.innerHeight - rect.top + 4}px`
  } else {
    panel.style.bottom = "auto"
    panel.style.top = `${rect.bottom + 4}px`
  }
}

// "All X" or a chosen few. Nothing ticked means all; the trigger says which. A picker with a parent (marketplaces under
// accounts) only offers what belongs to the parent's choice. Announces every change so the form can recount its split.
export default class extends Controller {
  static targets = ["trigger", "summary", "panel", "search", "item", "box", "none"]
  static values = { name: String, allLabel: String, noun: String, parent: String }

  connect() {
    this.parentIds = []
    this.render()
  }

  toggle() {
    this.panelTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.panelTarget.hidden = false
    place(this.panelTarget, this.triggerTarget)
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.searchTarget.focus({ preventScroll: true })
    this.reposition = () => place(this.panelTarget, this.triggerTarget)
    window.addEventListener("resize", this.reposition)
    window.addEventListener("scroll", this.reposition, true)
  }

  close() {
    if (this.reposition) {
      window.removeEventListener("resize", this.reposition)
      window.removeEventListener("scroll", this.reposition, true)
      this.reposition = null
    }
    this.panelTarget.hidden = true
    this.triggerTarget.setAttribute("aria-expanded", "false")
  }

  disconnect() {
    this.close()
  }

  closeOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  keydown(event) {
    if (event.key === "Escape" && !this.panelTarget.hidden) {
      event.stopPropagation()
      this.close()
      this.triggerTarget.focus()
    }
  }

  filter() {
    this.applyVisibility()
  }

  clear() {
    this.boxTargets.forEach((box) => { box.checked = false })
    this.changed()
  }

  changed() {
    this.render()
    window.dispatchEvent(new CustomEvent("multi-picker:changed", { detail: { name: this.nameValue, count: this.count, ids: this.checked.map((box) => box.value) } }))
  }

  // A parent picker changed: remember which of its choices apply, hide the rest and untick what is no longer offered.
  parentChanged(event) {
    if (!this.parentValue || event.detail.name !== this.parentValue) return

    this.parentIds = event.detail.ids
    this.boxTargets.forEach((box) => { if (!this.allowed(box.closest("li"))) box.checked = false })
    this.applyVisibility()
    this.changed()
  }

  allowed(item) {
    return !this.parentIds.length || this.parentIds.includes(item.dataset.group)
  }

  applyVisibility() {
    if (this.reposition) requestAnimationFrame(this.reposition)
    const needle = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    let shown = 0
    this.itemTargets.forEach((item) => {
      const visible = this.allowed(item) && (!needle || item.dataset.search.includes(needle))
      item.hidden = !visible
      item.dataset.allowed = this.allowed(item) ? "1" : "0"
      if (visible) shown++
    })
    this.noneTarget.hidden = shown > 0
  }

  get checked() {
    return this.boxTargets.filter((box) => box.checked)
  }

  // How many things this picker stands for: the ticked ones, or everything on offer when none is.
  get count() {
    const ticked = this.checked.length
    if (ticked) return ticked
    return this.itemTargets.filter((item) => this.allowed(item)).length
  }

  render() {
    const ticked = this.checked
    let text = this.allLabelValue
    if (ticked.length === 1) text = ticked[0].dataset.label
    else if (ticked.length > 1) text = `${ticked[0].dataset.label} +${ticked.length - 1} more`
    this.summaryTarget.textContent = text
    this.element.dataset.count = this.count
    this.applyVisibility()
  }
}
